<script lang="ts" setup>
import background from '~/assets/images/backgrounds/custom_task_editor.jpg';
import { Level, Sex } from '~/stores/game';
import type { CreateCustomTaskType, TaskType } from '~/types/tasks';

const props = defineProps<{
  originalTask?: TaskType;
}>();

const store = useMainStore();
const router = useRouter();
const toast = useCustomToast();
const confirm = useCustomConfirm();
const { t, tm, rt } = useI18n();

const { data: categories } = await useAsyncData(() => TaskAPI.categories());

const activeStep = ref(1);
const selectShortDialogVisible = ref(false);
const loading = ref(false);
const skipMediaConfirmationDialogVisible = ref(false);
const skipMediaConfirmed = ref(false);
const task = reactive<CreateCustomTaskType>(
  props.originalTask || {
    content: '',
    target: Sex.girl,
    level: Level.easy,
    categoryIds: [],
    media: '',
    settings: {
      public: true,
      shared: false,
    },
  },
);

const genderOptions = [
  { label: t('variables.target.girl'), value: Sex.girl },
  { label: t('variables.target.boy'), value: Sex.boy },
];

const levelOptions = [
  { label: t('variables.levels.0'), value: Level.easy },
  { label: t('variables.levels.1'), value: Level.average },
  { label: t('variables.levels.2'), value: Level.hot },
];

const commonValid = computed(() => !!task.content);
const categoriesValid = computed(() => !!task.categoryIds.length);
const mediaValid = computed(() => !!task.media);

const toggleCategory = (categoryId: number) => {
  if (task.categoryIds.includes(categoryId)) task.categoryIds.splice(task.categoryIds.indexOf(categoryId), 1);
  else task.categoryIds.push(categoryId);
};

const onDelete = () => {
  const taskId = props.originalTask?.id;
  if (loading.value || !taskId) return;
  loading.value = true;

  confirm.require({
    header: t('messages.delete_confirmation_title'),
    acceptLabel: t('messages.delete_confirmation_accept'),
    rejectLabel: t('messages.delete_confirmation_reject'),
    accept: () => {
      CustomTaskAPI.delete({ params: { taskId } })
        .then(() => {
          toast.add({
            severity: 'success',
            summary: t('messages.deleting_success'),
            life: 3000,
          });
          router.push('/tasks/custom');
        })
        .catch(() => {
          loading.value = false;
          toast.add({
            severity: 'error',
            summary: t('messages.deleting_error'),
            life: 5000,
          });
        });
    },
  });
};

const onSubmit = () => {
  if (loading.value) return;
  loading.value = true;

  if (props.originalTask) {
    CustomTaskAPI.update({
      body: task,
      params: { taskId: props.originalTask.id },
    })
      .then(() => {
        toast.add({
          severity: 'success',
          summary: t('messages.updating_success'),
          life: 3000,
        });
        router.push('/tasks/custom');
      })
      .catch(() => {
        loading.value = false;
        toast.add({
          severity: 'error',
          summary: t('messages.updating_error'),
          life: 5000,
        });
      });
  } else {
    CustomTaskAPI.create({ body: task })
      .then(() => {
        toast.add({
          severity: 'success',
          summary: t('messages.creating_success'),
          detail: t('messages.creating_success_detail'),
        });
        router.push('/tasks/custom');
      })
      .catch(() => {
        loading.value = false;
        toast.add({
          severity: 'error',
          summary: t('messages.creating_error'),
          life: 5000,
        });
      });
  }
};
</script>

<template>
  <main class="p-section" data-test-id="create-custom-task-page">
    <BasePageBackgroundImage :image="background" />
    <div class="page-overflow-6" />

    <div class="lg:container py-8">
      <Stepper v-model:value="activeStep" linear class="mx-auto max-w-200">
        <Card :pt="{ body: 'p-1!' }">
          <template #content>
            <StepList class="no-scroll">
              <Step
                v-for="(item, index) in tm('steps')"
                :key="index"
                :value="index + 1"
                :pt="{
                  number: 'transition duration-300',
                  title: 'transition duration-300',
                }"
              >
                {{ rt(item) }}
              </Step>
            </StepList>
          </template>
        </Card>
        <StepPanels class="p-0! pt-5!">
          <BaseToggleTransition :duration="400">
            <StepPanel v-if="activeStep === 1" v-slot="{ activateCallback }" :value="1" class="bg-transparent!">
              <Card
                :pt="{
                  body: 'p-4!',
                  content: 'flex flex-col gap-5',
                  footer: 'flex justify-end gap-3',
                }"
              >
                <template #content>
                  <div class="flex flex-col md:flex-row gap-5">
                    <div class="flex flex-col gap-2 md:w-1/2">
                      <label>{{ t('target_label') }}</label>
                      <SelectButton
                        v-model="task.target"
                        :options="genderOptions"
                        option-label="label"
                        option-value="value"
                        class="w-full"
                        :pt="{
                          pcToggleButton: {
                            root: {
                              class: 'flex-1',
                              'data-test-id': 'create-custom-task-target-option',
                            },
                          },
                        }"
                      />
                    </div>
                    <div class="flex flex-col gap-2 md:w-1/2">
                      <label>{{ t('level_label') }}</label>
                      <SelectButton
                        v-model="task.level"
                        :options="levelOptions"
                        option-label="label"
                        option-value="value"
                        class="w-full"
                        :pt="{
                          pcToggleButton: {
                            root: {
                              class: 'flex-1',
                              'data-test-id': 'create-custom-task-level-option',
                            },
                          },
                        }"
                      />
                    </div>
                  </div>

                  <div class="flex flex-col gap-2">
                    <label>{{ t('content_label') }}</label>
                    <Textarea
                      v-model="task.content"
                      auto-resize
                      spellcheck
                      rows="3"
                      class="block"
                      data-test-id="create-custom-task-content-field"
                    />
                  </div>
                </template>

                <template #footer>
                  <Button as="nuxt-link" to="/tasks/custom" :label="t('cancel')" severity="secondary" outlined />
                  <Button
                    @click="activateCallback(2)"
                    :disabled="!commonValid"
                    :label="t('next')"
                    data-test-id="create-custom-task-common-next"
                  />
                </template>
              </Card>
            </StepPanel>
            <StepPanel v-if="activeStep === 2" v-slot="{ activateCallback }" :value="2" class="bg-transparent!">
              <Card class="overflow-hidden" :pt="{ body: 'p-0!', footer: 'flex justify-end gap-3 p-4!' }">
                <template #content>
                  <Accordion :value="200">
                    <template v-for="group in categories" :key="group.id">
                      <AccordionPanel v-if="group.id !== 100" :value="group.id" class="border-none!">
                        <AccordionHeader class="rounded-none!">
                          {{ t(`categories.groups.${group.id}.label`) }}
                        </AccordionHeader>
                        <AccordionContent :pt="{ content: 'flex flex-col gap-3' }">
                          <template v-for="item in group.categories" :key="item.id">
                            <span v-if="!item.deprecated" :key="item.id" class="my-0 text-2xl flex items-center">
                              <span class="mr-1 text-lg w-fit">
                                {{ t(`categories.groups.${group.id}.items.${item.id}`) }}
                              </span>
                              <ToggleSwitch
                                @update:model-value="toggleCategory(item.id)"
                                :model-value="task.categoryIds.includes(item.id)"
                                class="ml-auto"
                                data-test-id="create-custom-task-category-option"
                              />
                            </span>
                          </template>
                        </AccordionContent>
                      </AccordionPanel>
                    </template>
                  </Accordion>
                </template>

                <template #footer>
                  <Button @click="activateCallback(1)" :label="t('prev')" severity="secondary" outlined />
                  <Button
                    @click="activateCallback(3)"
                    :disabled="!categoriesValid"
                    :label="t('next')"
                    data-test-id="create-custom-task-category-next"
                  />
                </template>
              </Card>
            </StepPanel>
            <StepPanel v-if="activeStep === 3" v-slot="{ activateCallback }" :value="3" class="bg-transparent!">
              <Card
                :pt="{
                  body: 'p-4!',
                  content: 'pt-3',
                  footer: 'flex justify-end gap-3',
                }"
              >
                <template #title>Выбор медиа</template>

                <template #content>
                  <template v-if="task.media">
                    <img :src="task.media" class="block w-full rounded-lg border border-solid border-surface-400" />
                    <Button
                      @click="selectShortDialogVisible = true"
                      :label="t('replace_media')"
                      severity="secondary"
                      class="w-full block mt-3 mb-3"
                    />
                  </template>
                  <button
                    v-else
                    @click="selectShortDialogVisible = true"
                    class="aspect-square max-h-120 block w-full bg-surface-600 rounded-lg border border-solid border-surface-400 p-0 outline-none cursor-pointer transition hover:opacity-70"
                    data-test-id="create-custom-task-select-media"
                  >
                    <i class="pi pi-plus text-2xl" />
                  </button>

                  <GameSelectShortDialog @select="task.media = $event.url" v-model:visible="selectShortDialogVisible" />

                  <DialogCustomTaskSkipMedia
                    @skip="
                      skipMediaConfirmed = true;
                      activateCallback(4);
                    "
                    v-model:visible="skipMediaConfirmationDialogVisible"
                  />
                </template>

                <template #footer>
                  <Button @click="activateCallback(2)" :label="t('prev')" severity="secondary" outlined />
                  <Button
                    @click="
                      mediaValid || skipMediaConfirmed
                        ? activateCallback(4)
                        : (skipMediaConfirmationDialogVisible = true)
                    "
                    :label="t('next')"
                    data-test-id="create-custom-task-media-next"
                  />
                </template>
              </Card>
            </StepPanel>
            <StepPanel v-if="activeStep === 4" v-slot="{ activateCallback }" :value="4" class="bg-transparent!">
              <Card :pt="{ body: 'p-4!', footer: 'flex justify-end gap-3' }">
                <template #title>{{ t('setting_title') }}</template>

                <template #content>
                  <div class="flex flex-col gap-4 pb-6 pt-4">
                    <span class="flex items-center">
                      <span class="mr-2 flex-shrink-1 text-base xl:text-lg flex-1">
                        {{ t('public_label') }}
                      </span>
                      <span class="flex items-center gap-2">
                        <BaseInfoDialog :title="t('instructions.public.title')">
                          {{ t('instructions.public.content') }}
                        </BaseInfoDialog>
                        <ToggleSwitch v-model="task.settings.public" class="ml-auto" />
                      </span>
                    </span>
                    <span class="flex items-center">
                      <span class="mr-2 flex-shrink-1 text-base xl:text-lg flex-1">
                        {{ t('shared_label') }}
                      </span>
                      <span class="flex items-center gap-2">
                        <BaseInfoDialog :title="t('instructions.shared.title')">
                          {{ t('instructions.shared.content') }}
                        </BaseInfoDialog>
                        <ToggleSwitch v-model="task.settings.shared" class="ml-auto" />
                      </span>
                    </span>
                  </div>
                </template>

                <template #footer>
                  <Button @click="activateCallback(3)" :label="t('prev')" severity="secondary" outlined />
                  <Button
                    @click="onSubmit"
                    :loading="loading"
                    :label="t('complete')"
                    data-test-id="create-custom-task-submit"
                  />
                </template>
              </Card>
            </StepPanel>
          </BaseToggleTransition>
        </StepPanels>

        <Button
          v-if="originalTask?.settings.userId === store.user.id"
          @click="onDelete"
          :loading="loading"
          severity="error"
          outlined
          :label="t('delete')"
          icon="pi pi-trash"
          class="w-full mt-4"
          data-test-id="create-custom-task-delete"
        />
      </Stepper>
    </div>
  </main>
</template>

<i18n src="#locales/categories.yaml"></i18n>
<i18n src="~/locales/global.yaml"></i18n>
<i18n>
en:
  messages:
    delete_confirmation_title: Are you sure you want to delete the task?
    delete_confirmation_accept: Confirm
    delete_confirmation_reject: Cancel
    deleting_success: Task successfully deleted
    deleting_error: An error occurred while deleting the task
    updating_success: Task successfully updated!
    updating_error: An error occurred while updating the task
    creating_success: Task successfully created!
    creating_success_detail: Now you can add it to the next game ;)
    creating_error: An error occurred while creating the task
  steps:
    - Basic
    - Categories
    - Media
    - Settings
  setting_title: Task settings
  target_label: Task for
  level_label: Level
  content_label: Task content
  public_label: Public tasks
  shared_label: Available to partner
  replace_media: Replace
  cancel: Cancel
  prev: Cancel
  next: Next
  complete: Complete
  delete: Delete
  instructions:
    public:
      title: Public tasks
      content: >
        Such tasks will participate in the games of all people together with tasks from the Sex Play team. Users will
        not see who this task is from. This is a great opportunity to help the project make it even better!


        Such tasks will pass moderation and after it will appear to all users. Therefore, we strongly ask you to do such
        tasks carefully and with soul💞
    shared:
      title: Available to partner
      content: >
        When you enable this setting, your partner will see these tasks in their list of tasks. They will not be able to
        delete or edit them, but you will be able to share ideas with each other.

ru:
  messages:
    delete_confirmation_title: Вы уверены, что хотите удалить задание?
    delete_confirmation_accept: Подтвердить
    delete_confirmation_reject: Отмена
    deleting_success: Задание успешно удалено
    deleting_error: Во время удаления задания произошла ошибка
    updating_success: Задание успешно обновлено!
    updating_error: Во время обновления задания произошла ошибка
    creating_success: Задание успешно создано!
    creating_success_detail: Теперь вы можете добавить его в следующую игру ;)
    creating_error: Во время создания задания произошла ошибка
  steps:
    - Базовое
    - Категории
    - Медиа
    - Настройки
  setting_title: Настройки задания
  target_label: Задание для
  level_label: Уровень
  content_label: Содержание задания
  public_label: Общедоступные задания
  shared_label: Доступно партнеру
  replace_media: Заменить
  cancel: Отменить
  prev: Отменить
  next: Далее
  complete: Завершить
  delete: Удалить
  instructions:
    public:
      title: Общедоступные задания
      content: >
        Такие задания будут участвовать в играх всех людей вместе с заданиями от команды Sex Play. Пользователи не будут
        видеть от кого это задание. Это отличная возможность помочь проекту сделать его еще лучше!


        Такие задания пройдут модерацию и после нее будут попадаться всем пользователям. Поэтому мы настоятельно просим
        вас делать такие задания аккуратно и с душой💞
    shared:
      title: Доступно партнеру
      content: >
        При включении этой настройки, ваш партнер будет видеть эти задания в списке своих заданий. У него не будет
        возможности их удалить или редактировать, но вы сможете так делиться идеями друг с другом.

de:
  messages:
    delete_confirmation_title: Sind Sie sicher, dass Sie die Aufgabe löschen möchten?
    delete_confirmation_accept: Bestätigen
    delete_confirmation_reject: Stornieren
    deleting_success: Die Aufgabe wurde erfolgreich gelöscht
    deleting_error: Beim Löschen des Jobs ist ein Fehler aufgetreten
    updating_success: Die Aufgabe wurde erfolgreich aktualisiert!
    updating_error: Beim Aktualisieren des Jobs ist ein Fehler aufgetreten
    creating_success: Die Aufgabe wurde erfolgreich erstellt!
    creating_success_detail: Jetzt können Sie es zu Ihrem nächsten Spiel hinzufügen ;)
    creating_error: Beim Erstellen des Jobs ist ein Fehler aufgetreten
  steps:
    - Basic
    - Kategorien
    - Medien
    - Einstellungen
  setting_title: Jobeinstellungen
  target_label: Auftrag für
  level_label: Ebene
  content_label: Inhalt der Aufgabe
  public_label: Öffentliche Quests
  shared_label: Verfügbar für Partner
  replace_media: Ersetzen
  cancel: Stornieren
  prev: Stornieren
  next: Nächste
  complete: Vollständig
  delete: Löschen
  instructions:
    public:
      title: Öffentliche Quests
      content: 'Solche Aufgaben werden zusammen mit Aufgaben des Teams Sex Play an den Spielen aller Menschen
        teilnehmen. Benutzer können nicht sehen, von wem diese Aufgabe stammt. Dies ist eine großartige Gelegenheit, das
        Projekt noch besser zu machen! Solche Aufgaben werden moderiert und stehen dann allen Nutzern zur Verfügung.
        Deshalb bitten wir Sie dringend, solche Aufgaben sorgfältig und mit Seele zu erledigen💞'
    shared:
      title: Verfügbar für Partner
      content: Wenn Sie diese Einstellung aktivieren, sieht Ihr Partner diese Aufgaben in seiner Aufgabenliste. Er kann sie
        nicht löschen oder bearbeiten, aber Sie können Ideen miteinander austauschen.

es:
  messages:
    delete_confirmation_title: ¿Está seguro de que desea eliminar la tarea?
    delete_confirmation_accept: Confirmar
    delete_confirmation_reject: Cancelar
    deleting_success: La tarea se eliminó correctamente.
    deleting_error: Se produjo un error al eliminar el trabajo.
    updating_success: ¡La tarea se ha actualizado correctamente!
    updating_error: Se produjo un error al actualizar el trabajo.
    creating_success: ¡La tarea se ha creado con éxito!
    creating_success_detail: Ahora puedes agregarlo a tu próximo juego;)
    creating_error: Se produjo un error al crear el trabajo.
  steps:
    - Básico
    - Categorías
    - Medios de comunicación
    - Ajustes
  setting_title: Configuraciones de trabajo
  target_label: tarea para
  level_label: Nivel
  content_label: Contenido de la tarea
  public_label: Misiones públicas
  shared_label: Disponible para asociarse
  replace_media: Reemplazar
  cancel: Cancelar
  prev: Cancelar
  next: Próximo
  complete: Completo
  delete: Borrar
  instructions:
    public:
      title: Misiones públicas
      content: Estas tareas participarán en los juegos de todas las personas junto con las tareas del equipo
        Sex Play. Los usuarios no verán de quién proviene esta tarea. ¡Esta es una gran oportunidad para ayudar
        a que el proyecto sea aún mejor! Estas tareas se moderarán y luego estarán disponibles para todos los usuarios.
        Por lo tanto, le instamos a que realice estas tareas con cuidado y con alma💞
    shared:
      title: Disponible para asociarse
      content: Cuando habilita esta configuración, su socio verá estas tareas en su lista de tareas. No podrá eliminarlos ni
        editarlos, pero podrán compartir ideas entre sí.

fr:
  messages:
    delete_confirmation_title: Êtes-vous sûr de vouloir supprimer la tâche ?
    delete_confirmation_accept: Confirmer
    delete_confirmation_reject: Annuler
    deleting_success: La tâche a été supprimée avec succès
    deleting_error: Une erreur s'est produite lors de la suppression du travail
    updating_success: La tâche a été mise à jour avec succès !
    updating_error: Une erreur s'est produite lors de la mise à jour du travail
    creating_success: La tâche a été créée avec succès !
    creating_success_detail: Vous pouvez maintenant l'ajouter à votre prochain jeu ;)
    creating_error: Une erreur s'est produite lors de la création du travail
  steps:
    - Basique
    - Catégories
    - Médias
    - Paramètres
  setting_title: Paramètres du travail
  target_label: Mission pour
  level_label: Niveau
  content_label: Contenu de la tâche
  public_label: Quêtes publiques
  shared_label: Disponible pour partenaire
  replace_media: Remplacer
  cancel: Annuler
  prev: Annuler
  next: Suivant
  complete: Complet
  delete: Supprimer
  instructions:
    public:
      title: Quêtes publiques
      content: Ces tâches participeront aux jeux de tous ainsi que les tâches de l'équipe Sex Play. Les
        utilisateurs ne verront pas de qui provient cette tâche. C’est une excellente opportunité de contribuer à rendre
        le projet encore meilleur ! Ces tâches feront l'objet d'une modération et seront ensuite disponibles pour tous
        les utilisateurs. Par conséquent, nous vous invitons à effectuer ces tâches avec soin et avec âme💞
    shared:
      title: Disponible pour partenaire
      content: Lorsque vous activez ce paramètre, votre partenaire verra ces tâches dans sa liste de tâches. Il ne pourra pas
        les supprimer ou les modifier, mais vous pourrez partager des idées entre vous.
</i18n>
